import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { createOrder, createStripeCheckout } from '@/lib/api';
import { toast } from 'sonner';
import { PageSEO } from '@/components/SEO';

// Define icon components outside to avoid re-creation on each render
const ApplePayIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const GooglePayIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
  </svg>
);

// Stripe-only payment options
const paymentOptions = [
  {
    value: 'card',
    label: 'Credit / Debit Card',
    icon: CreditCard,
    description: 'Visa, Mastercard, American Express'
  },
  {
    value: 'applepay',
    label: 'Apple Pay',
    icon: ApplePayIcon,
    description: 'Pay with Apple Pay'
  },
  {
    value: 'googlepay',
    label: 'Google Pay',
    icon: GooglePayIcon,
    description: 'Pay with Google Pay'
  }
];

export default function Checkout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Singapore'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.customer_name || !formData.customer_email || !formData.address) {
        toast.error('Please fill in all required fields');
        setLoading(false);
        return;
      }

      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        shipping_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country,
          phone: formData.phone
        },
        billing_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country,
          phone: formData.phone
        }
      };

      // Create order first
      const orderRes = await createOrder(orderData);
      const orderId = orderRes.data.order_id;

      // All payment methods go through Stripe Checkout
      const checkoutRes = await createStripeCheckout(orderId);
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.detail || 'Checkout failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="checkout-page">
      <PageSEO.checkout />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-medium mb-8">Checkout</h1>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              &times;
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Shipping Information */}
          <div className="bg-white border border-stone-200 rounded-lg p-6">
            <h2 className="text-xl font-medium mb-6">Shipping Information</h2>
            
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    required
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  rows={2}
                  data-testid="checkout-address"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    data-testid="checkout-city"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    data-testid="checkout-postal"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    data-testid="checkout-country"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white border border-stone-200 rounded-lg p-6">
            <h2 className="text-xl font-medium mb-6">Payment Method</h2>
            
            <RadioGroup 
              value={paymentMethod} 
              onValueChange={(value) => {
                setPaymentMethod(value);
                setError(null); // Clear error when changing payment method
              }}
              className="space-y-3"
              disabled={loading}
            >
              {paymentOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <label
                    key={option.value}
                    className={`flex items-center space-x-4 p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentMethod === option.value 
                        ? 'border-stone-900 bg-stone-50' 
                        : 'border-stone-200 hover:border-stone-400'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`payment-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} id={option.value} disabled={loading} />
                    <div className="flex-shrink-0">
                      <Icon className="h-5 w-5 text-stone-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-stone-500">{option.description}</p>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/cart')}
              disabled={loading}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Cart
            </Button>
            <Button
              type="submit"
              className="min-w-[200px]"
              disabled={loading}
              data-testid="checkout-submit"
            >
              {loading ? 'Processing...' : `Pay with ${paymentOptions.find(o => o.value === paymentMethod)?.label}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
